import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../utils/api';
import IdentityAuditPanel from './IdentityAuditPanel';

const labels = { website: 'Website', instagram_handle: 'Instagram', tiktok_handle: 'TikTok', linkedin_url: 'LinkedIn', youtube_url: 'YouTube', name: 'Brand name', former_names: 'Former names' };

function safeUrl(value) {
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) ? url.href : null;
  } catch { return null; }
}

function Value({ field, value }) {
  if (!value) return <span>Not set</span>;
  let url = value;
  if (field === 'instagram_handle') url = `https://www.instagram.com/${encodeURIComponent(value.replace(/^@/, ''))}/`;
  if (field === 'tiktok_handle') url = `https://www.tiktok.com/@${encodeURIComponent(value.replace(/^@/, ''))}`;
  if (field === 'website' && !value.includes('://')) url = `https://${value}`;
  const href = ['name', 'former_names'].includes(field) ? null : safeUrl(url);
  return href ? <a href={href} target="_blank" rel="noopener noreferrer">{value}</a> : <span>{value}</span>;
}

function ProposalCard({ item, busy, onDecide }) {
  const pending = item.status === 'pending';
  const [picks, setPicks] = useState(() => Object.fromEntries(
    Object.entries(item.changes).map(([field, change]) => [field, { apply: true, value: change.to ?? '', editing: false }]),
  ));
  const update = (field, patch) => setPicks(p => ({ ...p, [field]: { ...p[field], ...patch } }));
  const selected = Object.entries(picks).filter(([, pick]) => pick.apply);
  const edited = selected.some(([field, pick]) => pick.value !== (item.changes[field].to ?? ''));
  const everything = selected.length === Object.keys(picks).length && !edited;
  const approve = () => onDecide(item, 'approve', Object.fromEntries(selected.map(([field, pick]) => [field, pick.value])));
  const showCurrent = item.stale && pending;

  return (
    <article className="card" style={{ padding: 20, gap: 12 }}>
      <div><h3 className="o-card-head-title">{item.brand_name}</h3><p>{item.organization} · {item.source.replaceAll('_', ' ')} · {new Date(item.created_at).toLocaleString()} · {item.status}</p></div>
      {showCurrent && <p role="status">This brand changed after research ran, or was removed. Reject this suggestion and run research again; approval is disabled.</p>}
      {pending && !item.stale && <p>Untick a field to keep its current value, or use Edit to type the correct value before approving.</p>}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
          <thead><tr>{pending && <th style={{ padding: 8 }}>Apply</th>}<th style={{ padding: 8 }}>Field</th><th style={{ padding: 8 }}>At research time</th><th style={{ padding: 8 }}>Proposed</th>{showCurrent && <th style={{ padding: 8 }}>Current</th>}</tr></thead>
          <tbody>{Object.entries(item.changes).map(([field, change]) => {
            const pick = picks[field];
            const label = labels[field] || field;
            return <tr key={field} style={{ opacity: pending && !pick.apply ? 0.5 : 1 }}>
              {pending && <td style={{ padding: 8 }}><input type="checkbox" aria-label={`Apply ${label}`} checked={pick.apply} disabled={busy !== null || item.stale} onChange={e => update(field, { apply: e.target.checked })} /></td>}
              <th scope="row" style={{ padding: 8 }}>{label}</th>
              <td style={{ padding: 8, overflowWrap: 'anywhere' }}><Value field={field} value={change.from} /></td>
              <td style={{ padding: 8, overflowWrap: 'anywhere' }}>
                {pending && pick.editing
                  ? <input aria-label={`${label} value`} value={pick.value} disabled={busy !== null} onChange={e => update(field, { value: e.target.value, apply: true })} style={{ width: '100%', minWidth: 180 }} />
                  : <Value field={field} value={pending ? pick.value : change.to} />}
                {pending && !item.stale && <>
                  {' '}<button type="button" className="btn btn-secondary" style={{ padding: '2px 10px', fontSize: 12 }} disabled={busy !== null} onClick={() => update(field, { editing: !pick.editing })}>{pick.editing ? 'Done' : 'Edit'}</button>
                  {pick.value !== (change.to ?? '') && <>{' '}<em>edited</em></>}
                </>}
              </td>
              {showCurrent && <td style={{ padding: 8 }}><Value field={field} value={item.current?.[field]} /></td>}
            </tr>;
          })}</tbody>
        </table>
      </div>
      <p>{item.evidence.reason || 'Research proposed a different identity.'}</p>
      <details><summary>Research context and sources</summary>
        <p>These are research sources, not independent proof that the proposed account belongs to the brand. Check the links before approving.</p>
        {item.evidence.provider && <p>Provider: {item.evidence.provider}</p>}
        {item.evidence.description && <p>{item.evidence.description}</p>}
        {item.evidence.seed_website && <p>Research website: <Value field="website" value={item.evidence.seed_website} /></p>}
        {(item.evidence.citations || []).length ? <ul>{item.evidence.citations.map((url, i) => <li key={i}>{safeUrl(url) ? <a href={safeUrl(url)} target="_blank" rel="noopener noreferrer">{url}</a> : String(url)}</li>)}</ul> : <p>No source links were returned by this research run.</p>}
      </details>
      {pending ? <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary" disabled={busy !== null || item.stale || selected.length === 0} onClick={approve}>{everything ? 'Approve changes' : `Approve selected (${selected.length})`}</button>
        <button className="btn btn-secondary" disabled={busy !== null} onClick={() => onDecide(item, 'reject')}>Reject · keep current</button>
      </div> : <p>Reviewed {item.reviewed_at ? new Date(item.reviewed_at).toLocaleString() : ''}{item.reviewed_by ? ` by ${item.reviewed_by}` : ''}{item.review_note ? ` — ${item.review_note}` : ''}</p>}
    </article>
  );
}

export default function ResearchChangesTab() {
  const [status, setStatus] = useState('pending');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const generation = useRef(0);
  const load = useCallback(async () => {
    const request = ++generation.current;
    setLoading(true);
    try {
      const result = await api.getResearchChanges(status, offset);
      if (request === generation.current) setData(result);
    } catch (e) {
      if (request === generation.current) setError(e.message || 'Unable to load research changes.');
    } finally {
      if (request === generation.current) setLoading(false);
    }
  }, [status, offset]);
  useEffect(() => { setError(''); load(); return () => { generation.current += 1; }; }, [load]);

  const decide = async (item, action, values) => {
    if (action === 'approve') {
      const names = Object.keys(values).map(field => labels[field] || field).join(', ');
      if (!window.confirm(`Apply ${names} to ${item.brand_name} in ${item.organization}? Future collection will use the approved accounts. Unselected fields keep their current values.`)) return;
    }
    setBusy(item.id); setError(''); setMessage('');
    try {
      if (action === 'approve') await api.decideResearchChange(item.id, action, '', values);
      else await api.decideResearchChange(item.id, action);
      setMessage(action === 'approve' ? 'Selected changes approved and applied.' : 'Suggestion rejected. Current values kept.');
    } catch (e) {
      setError(e.message || 'Unable to save your decision.');
    } finally {
      await load(); setBusy(null);
    }
  };

  return (
    <section style={{ display: 'grid', gap: 16 }}>
      <IdentityAuditPanel onProgress={load} />
      <div className="card" style={{ padding: 20, gap: 12 }}>
        <h2 className="o-card-head-title">Research changes</h2>
        <p>Review proposed website, social account and brand identity changes across all organisations. Existing values stay active until approval.</p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <label>Status{' '}<select aria-label="Review status" value={status} disabled={busy !== null} onChange={(e) => { setStatus(e.target.value); setOffset(0); setMessage(''); }}>
            {['pending', 'approved', 'rejected', 'stale', 'all'].map(s => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
          </select></label>
          <button className="btn btn-secondary" disabled={loading || busy !== null} onClick={() => { setError(''); load(); }}>Refresh</button>
          <span>{data.total} {status === 'all' ? '' : status} proposals</span>
        </div>
      </div>
      {error && <div role="alert" className="card" style={{ padding: 16 }}>{error}</div>}
      {message && <div role="status">{message}</div>}
      {loading ? <p role="status">Loading research changes…</p> : data.items.length === 0 ? <div className="card" style={{ padding: 24 }}>No {status === 'all' ? '' : status} research changes to show.</div> : data.items.map(item => (
        <ProposalCard key={item.id} item={item} busy={busy} onDecide={decide} />
      ))}
      <div style={{ display: 'flex', gap: 12 }}>
        <button className="btn btn-secondary" disabled={loading || busy !== null || offset === 0} onClick={() => setOffset(Math.max(0, offset - 25))}>Previous</button>
        <button className="btn btn-secondary" disabled={loading || busy !== null || offset + 25 >= data.total} onClick={() => setOffset(offset + 25)}>Next</button>
      </div>
    </section>
  );
}
